import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";

import useIntelligentPricingReview from "../../data/useIntelligentPricingReview";
import usePartDetails from "../../data/usePartDetails";
import usePartMutations from "../../data/usePartMutations";
import usePartsLookupMutations from "../../data/usePartsLookupMutations";
import useTemplateProductDescription from "../../data/useTemplateProductDescription";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useUserLookup } from "../../hooks/useUserLookup";
import Modal from "../../layouts/Modal";
import { IPart } from "../../models/Part";
import { FormatType } from "../../models/EbayOffer";
import { IListingOptimisation, IStageListingOptimisationItem } from "../../models/listingOptimisation/IListingOptimisation";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import getLiveEbayItemNumber from "../../utils/getLiveEbayItemNumber";
import { summarisePrices } from "../../utils/intelligencePricing";
import { listingAgeTone } from "../../utils/listingAge";
import openInNewTab from "../../utils/openInNewTab";
import PriceFormat from "../../utils/priceFormat";
import { describeRelistingRule } from "../../utils/relistingRule";
import AiTitleGenerator from "../ebayListings/AiTitleGenerator";
import BulkUpdatePoliciesModal, { IBulkUpdatePolicies } from "../ebayListings/listedTable/BulkUpdatePoliciesModal";
import { computeEbayListingTitle } from "../ebayListings/EbayListingTitle";
import EditListingSpecifics from "../ebayListings/EditListingSpecifics";
import useNotification from "../notifications/useNotifications";
import LoadingPulse from "../shared/LoadingPulse";
import { GRADE_DEFINITIONS, GRADE_STYLES } from "../shared/PartGradeBadge";
import FocusDrawerHealthSection, { PartNumberField } from "./FocusDrawerHealthSection";
import FocusDrawerPriceSection from "./FocusDrawerPriceSection";
import IntelligenceReviewModal from "./IntelligenceReviewModal";
import { vehicleLine } from "./QueueRow";
import { computeQueueRowSignals, mergeRowInsights } from "./queueRowSignals";
import { DONE_SECTION_CLASSES, drawerSectionDone, drawerTodos } from "./drawerSectionState";
import DoneChip from "./DoneChip";
import useEbay from "../../data/useEbay";

/** The policy and location picks held in the drawer until Save. Null = keep what is live. */
interface IDraftPolicies {
  fulfillmentPolicyId: string | null;
  paymentPolicyId: string | null;
  returnPolicyId: string | null;
  locationKey: string | null;
}

const NO_POLICIES: IDraftPolicies = { fulfillmentPolicyId: null, paymentPolicyId: null, returnPolicyId: null, locationKey: null };

/** One line in the drawer's change list, with the way to take it back. */
interface IDrawerChange {
  key: string;
  label: string;
  from?: string | null;
  to: string;
  /** Already on the server as a staged change; otherwise it is only in the drawer until Save. */
  saved: boolean;
  remove: () => void;
}

export interface IFocusDrawerProps {
  part: IPart | null;
  open: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  /** Where the listing sits in the list being walked: "Listing 1 of 3 selected". */
  position: { index: number; total: number; source: "selected" | "page" } | null;
  optimisation: IListingOptimisation | undefined;
  buildCarryOverItem: (partId: string) => IStageListingOptimisationItem;
  onStage: (item: IStageListingOptimisationItem, onSuccess?: () => void) => void;
  staging: boolean;
  onDiscard: (partId: string) => void;
  discarding: boolean;
  onRelist: (part: IPart) => void;
  onMarkReviewed: (part: IPart, thenNext: boolean) => void;
  markingReviewed: boolean;
  onOpenPhotos: (part: IPart) => void;
  onSendToQc: (part: IPart) => void;
  onScrapOut: (part: IPart) => void;
}

const AGE_PILL: Record<string, string> = {
  fresh: "bg-green-100 text-green-700",
  stale: "bg-amber-100 text-amber-700",
  old: "bg-orange-100 text-orange-700",
  longTail: "bg-red-100 text-red-700",
};

const TITLE_LIMIT = 80;

const parsePrice = (value: string): number | null => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : null;
};

/**
 * One listing in focus: age, grade, title, price, listing health and what is staged, with the
 * actions that move it on. Grade, part numbers, photos and specifics save to the part straight
 * away; everything else is staged and reaches eBay only when the listing is relisted.
 */
export default function FocusDrawer({
  part,
  open,
  onClose,
  onPrev,
  onNext,
  position,
  optimisation,
  buildCarryOverItem,
  onStage,
  staging,
  onDiscard,
  discarding,
  onRelist,
  onMarkReviewed,
  markingReviewed,
  onOpenPhotos,
  onSendToQc,
  onScrapOut,
}: IFocusDrawerProps) {
  const partId = part?.id;
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const { findUser } = useUserLookup();
  const { partDetails, isLoading: detailsLoading } = usePartDetails(partId, open && !!partId);
  const { latestReview } = useIntelligentPricingReview(open ? partId : undefined, "FocusDrawer");
  const { intelligencePricingForPart } = usePartsLookupMutations(partId ?? "");
  const { patchPart } = usePartMutations();
  const { bulkUpdateProductDescription } = useTemplateProductDescription();
  const { policies: ebayPolicies, locations: ebayLocations } = useEbay({ enabled: open });
  const { saveHandlers: gradeHandlers } = useDefaultCRUDHandlers("Grade");
  const { saveHandlers: partNumberHandlers } = useDefaultCRUDHandlers("Part number");
  const { saveHandlers: descriptionHandlers } = useDefaultCRUDHandlers("Description");

  const fullPart = useMemo(() => (part ? mergeRowInsights(part, partDetails) : null), [part, partDetails]);
  const signals = useMemo(() => (fullPart ? computeQueueRowSignals(fullPart) : null), [fullPart]);
  const offer = fullPart?.offers?.[0];
  const liveTitle = offer?.title ?? "";

  const [draftTitle, setDraftTitle] = useState("");
  const [draftPrice, setDraftPrice] = useState("");
  const [applyImageRules, setApplyImageRules] = useState(false);
  const [removeBackgroundFirstImage, setRemoveBackgroundFirstImage] = useState(false);
  const [applyCategoryDefaults, setApplyCategoryDefaults] = useState(false);
  const [draftPolicies, setDraftPolicies] = useState<IDraftPolicies>(NO_POLICIES);
  const [specificsOpen, setSpecificsOpen] = useState(false);
  const [policiesOpen, setPoliciesOpen] = useState(false);
  const [reviewListOpen, setReviewListOpen] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [savingPartNumber, setSavingPartNumber] = useState<PartNumberField | null>(null);
  const gradeRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Start the draft from what is staged, else from the live listing, every time the focus moves.
  useEffect(() => {
    setDraftTitle(optimisation?.newTitle ?? liveTitle);
    setDraftPrice(optimisation?.newPrice != null ? optimisation.newPrice.toFixed(2) : "");
    setApplyImageRules(optimisation?.applyImageRules ?? false);
    setRemoveBackgroundFirstImage(optimisation?.removeBackgroundFirstImage ?? false);
    setApplyCategoryDefaults(optimisation?.applyCategoryDefaults ?? false);
    setDraftPolicies({
      fulfillmentPolicyId: optimisation?.newFulfillmentPolicyId ?? null,
      paymentPolicyId: optimisation?.newPaymentPolicyId ?? null,
      returnPolicyId: optimisation?.newReturnPolicyId ?? null,
      locationKey: optimisation?.newLocationKey ?? null,
    });
    panelRef.current?.scrollTo({ top: 0 });
  }, [partId, optimisation?.id, optimisation?.updated, liveTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onPrev, onNext]);

  if (!part || !fullPart || !signals) {
    return null;
  }

  const trimmedTitle = draftTitle.trim();
  const titleChanged = trimmedTitle.length > 0 && trimmedTitle !== liveTitle;
  const newPrice = parsePrice(draftPrice);
  const priceChanged = newPrice != null && newPrice !== signals.livePrice;
  const stagedTitle = optimisation?.newTitle ?? null;
  const stagedPrice = optimisation?.newPrice ?? null;
  const savedPolicies: IDraftPolicies = {
    fulfillmentPolicyId: optimisation?.newFulfillmentPolicyId ?? null,
    paymentPolicyId: optimisation?.newPaymentPolicyId ?? null,
    returnPolicyId: optimisation?.newReturnPolicyId ?? null,
    locationKey: optimisation?.newLocationKey ?? null,
  };
  const dirty =
    (titleChanged ? trimmedTitle : null) !== stagedTitle ||
    (priceChanged ? newPrice : null) !== stagedPrice ||
    applyImageRules !== (optimisation?.applyImageRules ?? false) ||
    removeBackgroundFirstImage !== (optimisation?.removeBackgroundFirstImage ?? false) ||
    applyCategoryDefaults !== (optimisation?.applyCategoryDefaults ?? false) ||
    draftPolicies.fulfillmentPolicyId !== savedPolicies.fulfillmentPolicyId ||
    draftPolicies.paymentPolicyId !== savedPolicies.paymentPolicyId ||
    draftPolicies.returnPolicyId !== savedPolicies.returnPolicyId ||
    draftPolicies.locationKey !== savedPolicies.locationKey;

  const buildItem = (): IStageListingOptimisationItem => ({
    ...buildCarryOverItem(part.id),
    newTitle: titleChanged ? trimmedTitle : null,
    newPrice: priceChanged ? newPrice : null,
    newFulfillmentPolicyId: draftPolicies.fulfillmentPolicyId,
    newPaymentPolicyId: draftPolicies.paymentPolicyId,
    newReturnPolicyId: draftPolicies.returnPolicyId,
    newLocationKey: draftPolicies.locationKey,
    applyImageRules,
    removeBackgroundFirstImage,
    applyCategoryDefaults,
  });

  // Everything the drawer will save, each with its own way back. An edit above shows up here the
  // moment it is made; Save sends the lot in one go, so there is no separate "stage" step.
  const policyName = (id: string | null) => {
    if (!id) return "—";
    const all = [...(ebayPolicies.data?.data?.fulfilmentPolicies ?? []), ...(ebayPolicies.data?.data?.paymentPolicies ?? []), ...(ebayPolicies.data?.data?.returnPolicies ?? [])];
    return all.find((policy) => policy.id === id)?.name ?? id;
  };
  const locationName = (key: string | null) => (key ? ebayLocations.data?.data?.locations?.find((location) => location.id === key)?.name ?? key : "—");
  const changes: IDrawerChange[] = [];
  if (titleChanged) {
    changes.push({ key: "title", label: "Title", from: liveTitle, to: trimmedTitle, saved: stagedTitle === trimmedTitle, remove: () => setDraftTitle(liveTitle) });
  }
  if (priceChanged) {
    changes.push({
      key: "price",
      label: "Price",
      from: signals.livePrice != null ? PriceFormat(signals.livePrice) : null,
      to: PriceFormat(newPrice!),
      saved: stagedPrice === newPrice,
      remove: () => setDraftPrice(""),
    });
  }
  const policyChange = (key: keyof IDraftPolicies, label: string, liveId: string | null | undefined, name: (id: string | null) => string) => {
    const id = draftPolicies[key];
    if (!id) return;
    changes.push({
      key,
      label,
      from: name(liveId ?? null),
      to: name(id),
      saved: savedPolicies[key] === id,
      remove: () => setDraftPolicies((prev) => ({ ...prev, [key]: null })),
    });
  };
  policyChange("fulfillmentPolicyId", "Shipping", offer?.fulfillmentPolicyId, policyName);
  policyChange("paymentPolicyId", "Payment", offer?.paymentPolicyId, policyName);
  policyChange("returnPolicyId", "Returns", offer?.returnPolicyId, policyName);
  policyChange("locationKey", "Location", offer?.locationKey, locationName);
  if (removeBackgroundFirstImage) {
    changes.push({ key: "bg", label: "Images", to: "Remove background on 1st image", saved: !!optimisation?.removeBackgroundFirstImage, remove: () => setRemoveBackgroundFirstImage(false) });
  }
  if (applyImageRules) {
    changes.push({ key: "rules", label: "Images", to: "Apply image rules", saved: !!optimisation?.applyImageRules, remove: () => setApplyImageRules(false) });
  }
  if (applyCategoryDefaults) {
    changes.push({ key: "defaults", label: "Defaults", to: "Apply category defaults", saved: !!optimisation?.applyCategoryDefaults, remove: () => setApplyCategoryDefaults(false) });
  }
  const unsavedCount = changes.filter((change) => !change.saved).length;
  // Saved changes taken out of the list: still on the server until Save sends the removal.
  const removedCount =
    (stagedTitle != null && !titleChanged ? 1 : 0) +
    (stagedPrice != null && !priceChanged ? 1 : 0) +
    (["fulfillmentPolicyId", "paymentPolicyId", "returnPolicyId", "locationKey"] as (keyof IDraftPolicies)[]).filter((key) => savedPolicies[key] != null && draftPolicies[key] == null).length +
    (optimisation?.removeBackgroundFirstImage && !removeBackgroundFirstImage ? 1 : 0) +
    (optimisation?.applyImageRules && !applyImageRules ? 1 : 0) +
    (optimisation?.applyCategoryDefaults && !applyCategoryDefaults ? 1 : 0);

  const hasAnythingToStage = (item: IStageListingOptimisationItem) =>
    item.newTitle != null ||
    item.newPrice != null ||
    item.newFulfillmentPolicyId != null ||
    item.newPaymentPolicyId != null ||
    item.newReturnPolicyId != null ||
    item.newLocationKey != null ||
    !!item.applyImageRules ||
    !!item.removeBackgroundFirstImage ||
    !!item.applyCategoryDefaults;

  // Save = stage everything in the change list in one call. With nothing left in the list but
  // something staged on the server, Save clears it (the same as Discard).
  const handleSave = (after?: () => void) => {
    const item = buildItem();
    if (!hasAnythingToStage(item)) {
      if (optimisation) onDiscard(part.id);
      else addNotification({ primaryText: "No changes to save", variant: "warn" });
      after?.();
      return;
    }
    onStage(item, after);
  };

  const handleRelistNow = () => {
    if (dirty) handleSave(() => onRelist(part));
    else onRelist(part);
  };

  // The policy picks join the change list; nothing is sent until Save.
  const handlePickPolicies = (policies: IBulkUpdatePolicies) => {
    setDraftPolicies((prev) => ({
      fulfillmentPolicyId: policies.fulfillmentPolicyId ?? prev.fulfillmentPolicyId,
      paymentPolicyId: policies.paymentPolicyId ?? prev.paymentPolicyId,
      returnPolicyId: policies.returnPolicyId ?? prev.returnPolicyId,
      locationKey: policies.locationKey ?? prev.locationKey,
    }));
    setPoliciesOpen(false);
  };

  const handleGrade = (grade: string | null) => {
    if ((fullPart.grade ?? null) === grade) return;
    patchPart.mutate({ id: part.id, grade, vehicleId: part.vehicleId }, gradeHandlers);
  };

  const handleSavePartNumber = (field: PartNumberField, value: string) => {
    setSavingPartNumber(field);
    patchPart.mutate(
      { id: part.id, [field]: value, vehicleId: part.vehicleId },
      {
        ...partNumberHandlers,
        onSettled: () => setSavingPartNumber(null),
      }
    );
  };

  const handleRegenerateDescription = () => {
    bulkUpdateProductDescription.mutate({ partIds: [part.id] }, descriptionHandlers);
  };

  // Writes a fresh lookup into every listings page in the cache, so the row and the drawer agree
  // without a refetch - the same thing the bulk Get Intelligence action did on the old table.
  const applyLookupToCache = (summary: { min: number; max: number; avg: number } | null, activeCount: number) => {
    queryClient.getQueriesData(["pagedTable"]).forEach(([key]) => {
      queryClient.setQueryData(key, (current: any) => {
        if (!current?.data?.rows) return current;
        return {
          ...current,
          data: {
            ...current.data,
            rows: current.data.rows.map((row: any) =>
              row.id === part.id
                ? {
                    ...row,
                    intelligenceMin: summary?.min ?? null,
                    intelligenceAverage: summary?.avg ?? null,
                    intelligenceMax: summary?.max ?? null,
                    intelligenceActiveCount: activeCount,
                    lastIntelligenceLookup: new Date().toISOString(),
                  }
                : row
            ),
          },
        };
      });
    });
    queryClient.invalidateQueries(["partDetails", part.id]);
  };

  const handleRefreshLookup = async () => {
    setRefreshBusy(true);
    try {
      const response = await intelligencePricingForPart.mutateAsync(part.id);
      const results = response?.data ?? [];
      applyLookupToCache(summarisePrices(results), results.length);
      addNotification({
        primaryText: results.length === 0 ? "No intelligence results found" : `Intelligence updated from ${results.length} listings`,
        variant: results.length === 0 ? "warn" : "success",
      });
    } catch {
      addNotification({ primaryText: "Intelligence lookup failed", variant: "error" });
    } finally {
      setRefreshBusy(false);
    }
  };

  const name = part.category?.name || part.title || "Part";
  const liveItemNumber = getLiveEbayItemNumber(fullPart);
  const reviewedBy = part.listingReviewedBy ? findUser(part.listingReviewedBy)?.fullname : null;
  const listedBy = offer?.publishedBy ? findUser(offer.publishedBy)?.fullname : null;
  const firstTone = listingAgeTone(signals.firstListedDays);
  const thisTone = listingAgeTone(signals.listingDays);
  const rule = part.relistingRule;
  const wilmaTitle = (computeEbayListingTitle(fullPart, null, true) ?? "").trim();
  const gradeKey = (fullPart.grade ?? "").trim().toUpperCase().charAt(0);
  const currentGrade = GRADE_DEFINITIONS.find((definition) => definition.grade === gradeKey);

  const busy = staging || refreshBusy;

  // The listing health and the to-dos are judged on the listing as it will be once the pending
  // changes go out, so staging a price inside 10% of the market ticks the price criterion at once.
  // The Price section keeps the live signals: it is there to compare live against the market.
  // The draft is the truth here: taking a saved price out of the list drops it from the score at
  // once, even though the server still holds it until Save sends the removal.
  const pendingPrice = priceChanged ? newPrice : null;
  const effectivePart: IPart =
    pendingPrice != null && offer && pendingPrice !== signals.livePrice
      ? {
          ...fullPart,
          offers: [
            offer.format === FormatType.Auction ? { ...offer, auctionStartPrice: pendingPrice } : { ...offer, buyItNowPrice: pendingPrice },
            ...(fullPart.offers?.slice(1) ?? []),
          ],
        }
      : fullPart;
  const effectiveSignals = effectivePart === fullPart ? signals : computeQueueRowSignals(effectivePart);
  const healthIncludesChanges = effectivePart !== fullPart;

  // Each section goes green once there is nothing left to do in it. An edit counts as soon as it
  // is made; the chip says "not saved" until Save has sent it.
  const sectionInputs = {
    signals: effectiveSignals,
    grade: fullPart.grade,
    stagedTitle: titleChanged ? trimmedTitle : null,
    stagedPrice: priceChanged ? newPrice : null,
    partProLookupRun: !!fullPart.lastPartLookupChecked,
  };
  const done = drawerSectionDone(sectionInputs);
  const todos = drawerTodos(sectionInputs);
  const titleUnsaved = titleChanged && stagedTitle !== trimmedTitle;
  const priceUnsaved = priceChanged && stagedPrice !== newPrice;
  // Relisting is the last step: once every other to-do is ticked and nothing is waiting to be
  // saved, the Changes section says so and hands over to Relist.
  const readyToRelist =
    !signals.relistBlocked && unsavedCount === 0 && removedCount === 0 && todos.filter((todo) => todo.key !== "relist").every((todo) => todo.done);

  const agePill = (days: number | null, tone: string | null) => (
    <span className={classNames("rounded-full px-2 py-0.5 text-sm font-bold tabular-nums", tone ? AGE_PILL[tone] : "bg-gray-100 text-gray-500")}>
      {days != null ? `${days}d` : "—"}
    </span>
  );

  return (
    <>
      <Modal key={`specifics-${part.id}`} open={specificsOpen} setOpen={setSpecificsOpen} width="max-w-7xl">
        {specificsOpen && <EditListingSpecifics part={fullPart} />}
      </Modal>

      <BulkUpdatePoliciesModal
        open={policiesOpen}
        onClose={() => setPoliciesOpen(false)}
        onSubmit={handlePickPolicies}
        isLoading={false}
        title="Change policies"
        description="The policies you pick join the change list below and are applied when the listing is relisted - nothing is sent to eBay yet. Leave a field empty to keep what is live."
        submitLabel="Add to changes"
        showLocation={true}
      />

      <IntelligenceReviewModal
        open={reviewListOpen}
        setOpen={setReviewListOpen}
        review={latestReview.data?.data}
        isLoading={latestReview.isLoading && latestReview.fetchStatus !== "idle"}
        isError={latestReview.isError}
      />

      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-30" onClose={onClose}>
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-900/35" />
          </Transition.Child>

          <div className="fixed inset-y-0 right-0 flex max-w-full">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-out duration-200"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-150"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="flex h-full w-screen max-w-[36rem] flex-col bg-white shadow-2xl">
                <header className="space-y-2.5 border-b border-gray-200 px-6 pb-4 pt-5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      {position ? `Listing ${position.index + 1} of ${position.total} ${position.source === "selected" ? "selected" : "on this page"}` : "Listing"}
                    </span>
                    <div className="inline-flex items-center gap-1">
                      <button type="button" onClick={onPrev} aria-label="Previous listing" className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">
                        <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button type="button" onClick={onNext} aria-label="Next listing" className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">
                        <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button type="button" onClick={onClose} aria-label="Close" className="ml-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100">
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                    {name}
                  </Dialog.Title>

                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                    {part.vehicle?.vrm && (
                      <span className="rounded-md bg-slate-900 px-2 py-0.5 font-mono text-[13px] font-semibold tracking-wider text-amber-300">{part.vehicle.vrm}</span>
                    )}
                    <span>{vehicleLine(part)}</span>
                    {part.tagNumber && (
                      <>
                        <span className="text-gray-400">·</span>
                        <span>{part.tagNumber}</span>
                      </>
                    )}
                    {part.warehouseLocation?.displayName && (
                      <>
                        <span className="text-gray-400">·</span>
                        <span>{part.warehouseLocation.displayName}</span>
                      </>
                    )}
                    {liveItemNumber && (
                      <button
                        type="button"
                        onClick={() => openInNewTab(`https://www.ebay.co.uk/itm/${liveItemNumber}`)}
                        className="ml-auto inline-flex items-center gap-1 font-medium text-primary-700 hover:text-primary-900"
                      >
                        View on eBay
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500">
                    <span>
                      <strong className="text-gray-700">
                        {part.listingReviewedOn ? `Reviewed ${formatDateTime(part.listingReviewedOn, DateTime.DATE_MED)}${reviewedBy ? ` by ${reviewedBy}` : ""}` : "Never reviewed"}
                      </strong>
                      {part.lastIntelligenceLookup ? ` · last intelligent pricing ${formatDateTime(part.lastIntelligenceLookup, DateTime.DATE_MED)}` : ""}
                      {optimisation ? ` · staged ${formatDateTime(optimisation.updated ?? optimisation.created, DateTime.DATE_MED)}` : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => onMarkReviewed(part, false)}
                      disabled={markingReviewed}
                      className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      Mark reviewed
                    </button>
                  </div>
                </header>

                <div ref={panelRef} className="min-h-0 flex-1 overflow-y-auto">
                  <section aria-label="Age" className="space-y-2.5 border-b border-gray-200 px-6 py-4">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="flex flex-col gap-1 rounded-lg bg-gray-50 px-3 py-2.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">First listed</span>
                        <span>{agePill(signals.firstListedDays, firstTone)}</span>
                        <span className="text-[11px] text-gray-500">{formatDateTime(part.originalListedDate ?? offer?.listingStarted, DateTime.DATE_MED, "—")}</span>
                      </div>
                      <div className="flex flex-col gap-1 rounded-lg bg-gray-50 px-3 py-2.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">This listing</span>
                        <span>{agePill(signals.listingDays, thisTone)}</span>
                        <span className="text-[11px] text-gray-500">
                          {listedBy ? `by ${listedBy}, ` : ""}
                          {formatDateTime(offer?.listingStarted, DateTime.DATE_MED, "—")}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1 rounded-lg bg-gray-50 px-3 py-2.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Relisted</span>
                        <span>
                          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-sm font-bold tabular-nums text-slate-800">{signals.relistCount}×</span>
                        </span>
                        <span className="text-[11px] text-gray-500">{signals.relistCount > 0 ? "no sale yet" : "never relisted"}</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500">{describeRelistingRule(rule, { includeName: true }) ?? "No auto-relist rule on this category"}</p>
                    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        To do · {todos.filter((todo) => !todo.done).length} of {todos.length} left
                      </span>
                      <ul className="mt-1 space-y-1">
                        {todos.map((todo) => (
                          <li key={todo.key} className={classNames("flex items-start gap-1.5 text-xs", todo.done ? "text-gray-400" : todo.key === "blocked" ? "font-semibold text-red-700" : "text-gray-800")}>
                            {todo.done ? (
                              <CheckCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600" aria-hidden="true" />
                            ) : (
                              <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full border border-gray-400" aria-hidden="true" />
                            )}
                            <span className={todo.done ? "line-through" : ""}>{todo.label}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>

                  <section ref={gradeRef} aria-label="Grade" className={classNames("space-y-2 border-b px-6 py-4", done.grade ? DONE_SECTION_CLASSES : "border-gray-200")}>
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="inline-flex items-center gap-2 text-[13px] font-semibold text-gray-900">
                        Grade
                        {done.grade ? <DoneChip /> : null}
                      </h3>
                      <div className="inline-flex gap-1">
                        {GRADE_DEFINITIONS.map((definition) => {
                          const active = definition.grade === gradeKey;
                          return (
                            <button
                              key={definition.grade}
                              type="button"
                              onClick={() => handleGrade(definition.grade)}
                              disabled={patchPart.isLoading}
                              aria-pressed={active}
                              title={definition.title}
                              className={classNames(
                                "inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ring-1 ring-inset transition disabled:opacity-50",
                                active ? GRADE_STYLES[definition.grade] : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"
                              )}
                            >
                              {definition.grade}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500">
                      {currentGrade ? `${currentGrade.grade} · ${currentGrade.title} — ${currentGrade.description} ` : "No grade yet. "}
                      Saves to the part straight away.
                    </p>
                  </section>

                  <section aria-label="Title" className={classNames("space-y-2 border-b px-6 py-4", done.title ? DONE_SECTION_CLASSES : "border-gray-200")}>
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="inline-flex items-center gap-2 text-[13px] font-semibold text-gray-900">
                        Title
                        {done.title ? <DoneChip unsaved={titleUnsaved} /> : null}
                      </h3>
                      {titleChanged ? (
                        stagedTitle === trimmedTitle ? (
                          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800">Saved · differs from live</span>
                        ) : (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">Changed · not saved</span>
                        )
                      ) : null}
                    </div>
                    <label className="block">
                      <span className="flex items-center justify-between text-[11px] text-gray-500">
                        <span>eBay title</span>
                        <span className={classNames("tabular-nums", trimmedTitle.length > TITLE_LIMIT ? "font-semibold text-red-600" : "")}>
                          {trimmedTitle.length} / {TITLE_LIMIT}
                        </span>
                      </span>
                      <textarea
                        value={draftTitle}
                        onChange={(e) => setDraftTitle(e.target.value)}
                        rows={2}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      />
                    </label>
                    {detailsLoading && !partDetails ? (
                      <LoadingPulse />
                    ) : (
                      <div className="rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">Wilma title</span>
                        <div className="mt-1 flex items-start gap-2">
                          <p className="min-w-0 flex-1 text-xs text-gray-800">{wilmaTitle || "No category title structure to build from"}</p>
                          <button
                            type="button"
                            onClick={() => setDraftTitle(wilmaTitle)}
                            disabled={!wilmaTitle}
                            className="inline-flex h-7 shrink-0 items-center rounded-md border border-sky-300 bg-white px-2.5 text-[11px] font-semibold text-sky-800 hover:bg-sky-100 disabled:opacity-50"
                          >
                            Use
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <AiTitleGenerator part={fullPart} onApply={(title) => setDraftTitle(title)} />
                    </div>
                  </section>

                  <FocusDrawerPriceSection
                    part={fullPart}
                    signals={signals}
                    draftPrice={draftPrice}
                    onDraftPriceChange={setDraftPrice}
                    stagedPrice={stagedPrice}
                    review={latestReview.data?.data}
                    onRefreshLookup={handleRefreshLookup}
                    refreshBusy={refreshBusy}
                    onSeeListings={() => setReviewListOpen(true)}
                    done={done.price}
                    unsaved={priceUnsaved}
                  />

                  <FocusDrawerHealthSection
                    part={fullPart}
                    signals={effectiveSignals}
                    includesChanges={healthIncludesChanges}
                    done={done.health}
                    optimisation={optimisation}
                    onSavePartNumber={handleSavePartNumber}
                    savingPartNumber={savingPartNumber != null}
                    onManagePhotos={() => onOpenPhotos(part)}
                    onOpenSpecifics={() => setSpecificsOpen(true)}
                    onChangePolicies={() => setPoliciesOpen(true)}
                    onRegenerateDescription={handleRegenerateDescription}
                    regenerating={bulkUpdateProductDescription.isLoading}
                    onSetGrade={() => gradeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  />

                  <section
                    aria-label="Changes to save"
                    className={classNames("space-y-2 border-b px-6 py-4", readyToRelist ? DONE_SECTION_CLASSES : "border-sky-200 bg-sky-50")}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h3 className={classNames("inline-flex items-center gap-2 text-[13px] font-semibold", readyToRelist ? "text-green-900" : "text-sky-900")}>
                        Changes
                        {readyToRelist ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                            <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                            All done · ready to relist
                          </span>
                        ) : changes.length > 0 ? (
                          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800">
                            {changes.length}
                            {unsavedCount > 0 ? ` · ${unsavedCount} not saved` : " · saved"}
                          </span>
                        ) : null}
                      </h3>
                      {(changes.length > 0 || optimisation) && (
                        <button
                          type="button"
                          onClick={() => {
                            changes.forEach((change) => change.remove());
                            if (optimisation) onDiscard(part.id);
                          }}
                          disabled={discarding}
                          className="h-7 px-2 text-[11px] font-semibold text-red-700 hover:underline disabled:opacity-50"
                        >
                          Discard all
                        </button>
                      )}
                    </div>
                    {changes.length > 0 ? (
                      <ul className="space-y-1 text-xs">
                        {changes.map((change) => (
                          <li key={change.key} className="flex items-start gap-2 rounded-md bg-white/70 px-2 py-1.5">
                            <span className="w-16 shrink-0 font-medium text-sky-700">{change.label}</span>
                            <span className="min-w-0 flex-1 truncate text-slate-900" title={change.from ? `${change.from} → ${change.to}` : change.to}>
                              {change.from ? <span className="text-gray-500 line-through">{change.from}</span> : null}
                              {change.from ? " → " : ""}
                              {change.to}
                            </span>
                            {!change.saved ? <span className="shrink-0 rounded-full bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800">not saved</span> : null}
                            <button
                              type="button"
                              onClick={change.remove}
                              aria-label={`Remove the ${change.label.toLowerCase()} change`}
                              title="Remove this change"
                              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-700"
                            >
                              <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={classNames("text-xs", readyToRelist ? "text-green-800" : "text-sky-800")}>
                        {readyToRelist
                          ? "Nothing left to change. Relist now to put it back on eBay as a fresh listing."
                          : "No changes yet. Edit the title, price or policies above and they appear here."}
                      </p>
                    )}
                    {removedCount > 0 && (
                      <p className="text-[11px] text-amber-800">
                        {removedCount} saved change{removedCount === 1 ? "" : "s"} taken out — still on the listing until you Save.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-sky-900">
                      <label className="inline-flex items-center gap-1.5">
                        <input type="checkbox" checked={removeBackgroundFirstImage} onChange={(e) => setRemoveBackgroundFirstImage(e.target.checked)} className="h-3.5 w-3.5 rounded border-sky-300 text-primary-600" />
                        Remove background on 1st image
                      </label>
                      <label className="inline-flex items-center gap-1.5">
                        <input type="checkbox" checked={applyImageRules} onChange={(e) => setApplyImageRules(e.target.checked)} className="h-3.5 w-3.5 rounded border-sky-300 text-primary-600" />
                        Apply image rules
                      </label>
                      <label className="inline-flex items-center gap-1.5">
                        <input type="checkbox" checked={applyCategoryDefaults} onChange={(e) => setApplyCategoryDefaults(e.target.checked)} className="h-3.5 w-3.5 rounded border-sky-300 text-primary-600" />
                        Apply category defaults
                      </label>
                    </div>
                    <p className="text-[11px] text-sky-700">Save keeps these against the listing; nothing is sent to eBay until you relist. Grade, part numbers, photos and specifics save to the part straight away.</p>
                  </section>
                </div>

                <footer className="space-y-2.5 border-t border-gray-200 bg-white px-6 pb-4 pt-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleSave()}
                      disabled={busy || !dirty}
                      className="inline-flex h-10 items-center gap-1.5 rounded-md bg-primary-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {staging ? <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                      {unsavedCount > 0 && removedCount > 0
                        ? `Save ${unsavedCount} · remove ${removedCount}`
                        : unsavedCount > 0
                          ? `Save ${unsavedCount} change${unsavedCount === 1 ? "" : "s"}`
                          : removedCount > 0
                            ? `Save · remove ${removedCount}`
                            : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={handleRelistNow}
                      disabled={busy || signals.relistBlocked}
                      title={signals.relistBlocked ? signals.relistErrors.join("; ") : undefined}
                      className={classNames(
                        "inline-flex h-10 items-center gap-1.5 rounded-md bg-green-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50",
                        readyToRelist ? "ring-2 ring-green-300 ring-offset-1" : ""
                      )}
                    >
                      <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                      Relist now
                    </button>
                    <button
                      type="button"
                      onClick={() => onMarkReviewed(part, true)}
                      disabled={markingReviewed}
                      className="inline-flex h-10 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      Reviewed, next
                    </button>
                    <button type="button" onClick={onNext} className="ml-auto text-[13px] font-medium text-primary-700 hover:text-primary-900">
                      Skip ↓
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Part</span>
                    <button
                      type="button"
                      onClick={() => onSendToQc(part)}
                      className="inline-flex h-[34px] items-center gap-1.5 rounded-md border border-indigo-200 bg-white px-3 text-[13px] font-semibold text-indigo-800 hover:bg-indigo-50"
                    >
                      <ClipboardDocumentCheckIcon className="h-4 w-4" aria-hidden="true" />
                      Send to QC
                    </button>
                    <button
                      type="button"
                      onClick={() => onScrapOut(part)}
                      className="inline-flex h-[34px] items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 text-[13px] font-semibold text-red-700 hover:bg-red-50"
                    >
                      Scrap out
                    </button>
                    <span className="text-[11px] text-gray-500">Both ask to confirm. Scrap out queues the tag for the yard app; the listing ends when it is scanned.</span>
                  </div>
                </footer>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
